import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShare } from '@fortawesome/pro-solid-svg-icons'

import {
  Box,
  Button,
  DashboardModule,
  Grid,
  SectionDivider,
} from '@domain-group/suit-theme'

import ConversationContainer from '@components/ConversationContainer'
import OrderItemHeader from '@components/OrderItemHeader'

import { getAttachmentType } from '@functions/attachment'
import { confirmAction } from '@functions/entity'

import useCurrentUser from '@hooks/useCurrentUser'

import type { OrderItemModel } from '@models/types'

import AttachmentPreview from './components/AttachmentPreview'
import AttachmentTile from './components/AttachmentTile'
import OrderItemBulkActions from './components/OrderItemBulkActions'

import useMediaReviewerOrderItem from './hooks/useMediaReviewerOrderItem'

type OrderItemProps = {
  orderItem: OrderItemModel,
}

const OrderItem = (params: OrderItemProps) => {
  const { orderItem } = params

  const mediaReviewerOrderItemPayload = useMediaReviewerOrderItem({ orderItem })

  const {
    callbacks: {
      copyToCampaign,
    },
    commentMode,
    filteredAttachments,
    hasFilteredAttachments,
    loading,
    selectedAttachmentId,
    service,
  } = mediaReviewerOrderItemPayload

  const hasImages = filteredAttachments.some(attachment => getAttachmentType(attachment) === 'image')

  const currentUserPayload = useCurrentUser()
  const { isProviderUser } = currentUserPayload

  const orderItemAttachmentIds = filteredAttachments.map(attachment => attachment.id)

  const copyToCampaignParams = {
    callbacks: {
      actionFn: copyToCampaign,
    },
    options: {
      confirmButtonColor: '#43ac6a',
      entityParams: orderItemAttachmentIds,
      icon: 'success' as 'success',
    },
    strings: {
      actionString: 'Copy to',
      confirmString: 'Are you sure? It can take up to 5 minutes for images to be processed.',
      entityName: 'campaign',
      toastText: 'Copied Media to Campaign',
    },
  }

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      customHeader={(
        <OrderItemHeader
          orderItem={orderItem}
          // @ts-expect-error TS2739
          service={service}
        >
          {hasImages && !isProviderUser && (
            <Button
              buttonStyle="primaryCreate"
              icon={<FontAwesomeIcon icon={faShare} />}
              iconMargin="medium"
              // @ts-expect-error TS2322
              marginRight="medium"
              marginY="auto"
              onClick={() => confirmAction(copyToCampaignParams)}
              size="small"
              width={['40%', 'fit-content']}
            >
              Add All to Campaign
            </Button>
          )}
        </OrderItemHeader>
      )}
    >
      {!loading && !hasFilteredAttachments && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Media Present
          </DashboardModule.BlankState.Title>
        </DashboardModule.BlankState>
      )}

      {!!selectedAttachmentId && (
        <AttachmentPreview orderItemPayload={mediaReviewerOrderItemPayload} />
      )}

      {commentMode && (
        <>
          <SectionDivider>CHANGES REQUESTED</SectionDivider>
          <ConversationContainer
            // @ts-expect-error TS2322
            borderColor="lineColor"
            boxProps={{
              borderBottom: '1px solid',
              borderColor: 'lineColor',
              padding: 'large',
              paddingTop: 0,
            }}
            commentsBoxProps={{ maxHeight: '400px' }}
            conversation={{
              subject_id: selectedAttachmentId,
              subject_type: 'Attachment',
            }}
            requestOptions={{
              include_conversation_comments: true,
            }}
          />
        </>
      )}

      {!loading && hasFilteredAttachments && (
        <Grid
          // @ts-expect-error TS2322
          gridGap="medium"
          gridAutoRows="1fr"
          gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))"
          gridTemplateRows="repeat(auto-fill, minmax(140px, 1fr))"
          padding="large"
        >
          {filteredAttachments.map((attachment, index) => (
            <AttachmentTile
              attachment={attachment}
              index={index}
              isSelected={attachment.id === selectedAttachmentId}
              key={attachment.id}
              orderItemPayload={mediaReviewerOrderItemPayload}
            />
          ))}
        </Grid>
      )}

      {hasImages && (
        <OrderItemBulkActions orderItemPayload={mediaReviewerOrderItemPayload} />
      )}
    </DashboardModule>
  )
}

export default OrderItem
